import React, { useEffect } from "react";
import FileRow from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-row.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import {
  BucketObject,
  BucketObjectTypes,
  FileBrowserPanel,
  keyOf,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { DataSourceProvider } from "common";
import { classNames } from "@via/utils/helpers.tsx";

type FileListVariant = "default" | "dataPage";

interface FileListProps {
  dataSourceProvider: DataSourceProvider;
  items: BucketObject[];
  selectedKeys: Set<string>;
  onToggleSelect: (item: BucketObject) => void;
  onClickSelected: (item: BucketObject) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  isGetFolderListLoading: boolean;
  disableFolderSelection?: boolean;
  hasParentRowChecked: boolean;
  setHasParentRowChecked: (item: boolean) => void;
  /** Layout variant: 'default' for modals/downloads, 'dataPage' for data page with flex layout */
  variant?: FileListVariant;
}

const parentDir = {
  id: "..",
  displayName: "..",
  name: "..",
  bucketName: "",
  path: "Go to parent directory",
  size: null,
  dateModified: null,
  type: BucketObjectTypes.Folder,
};

const FileList: React.FC<FileListProps> = ({
  dataSourceProvider,
  items,
  selectedKeys,
  onToggleSelect,
  onClickSelected,
  onSelectAll,
  onClearSelection,
  isGetFolderListLoading,
  disableFolderSelection,
  hasParentRowChecked,
  setHasParentRowChecked,
  variant = "default",
}) => {
  const filesCount = items.length;
  const selectedCount = selectedKeys.size;
  const isDataPage = variant === "dataPage";

  useEffect(() => {
    if (dataSourceProvider === DataSourceProvider.BaseSpace) {
      parentDir.path = "";
    } else {
      parentDir.path = "Go to parent directory";
    }
  }, [dataSourceProvider]);

  return (
    <FileBrowserPanel
      className={classNames(
        "divide-y",
        isDataPage ? "h-full flex flex-col overflow-hidden" : ""
      )}>
      <div
        className={classNames(
          "flex items-center justify-between px-4 py-3",
          isDataPage ? "shrink-0" : ""
        )}>
        <div className="text-sm text-gray-600">
          Showing {items.length} item{items.length === 1 ? "" : "s"} —{" "}
          {selectedCount} selected
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="selectAllFilesButton"
            onClick={onSelectAll}
            className="rounded-lg border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Select All ({filesCount})
          </button>
          <button
            data-testid="clearSelectionButton"
            onClick={onClearSelection}
            className="rounded-lg border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Clear Selection
          </button>
        </div>
      </div>

      <div
        className={classNames(
          "overflow-auto",
          isDataPage ? "flex-1 min-h-0" : "max-h-[70vh]"
        )}>
        <FileRow
          key={"parent-dir"}
          item={parentDir}
          checked={hasParentRowChecked}
          onToggle={() => {
            if (hasParentRowChecked) {
              onClearSelection();
            } else {
              onSelectAll();
            }
            setHasParentRowChecked(!hasParentRowChecked);
          }}
          onClick={() => onClickSelected(parentDir)}
          disabled={false}
        />
        {items.map(item => {
          const k = keyOf(item);
          const shouldDisable = disableFolderSelection
            ? item.type === "folder"
            : false;
          return isGetFolderListLoading ? (
            <SkeletonLoader key={k} count={1} />
          ) : (
            <FileRow
              key={k}
              item={item}
              checked={selectedKeys.has(k)}
              disabled={item.id === ".." || shouldDisable}
              onToggle={() => onToggleSelect(item)}
              onClick={() => onClickSelected(item)}
            />
          );
        })}
      </div>
    </FileBrowserPanel>
  );
};

export default FileList;
